import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChange,
  SimpleChanges
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { FilterType } from '@core/enums';

export type FilterInputType = Exclude<FilterType, FilterType.NONE>;

export type FilterInputOption = {
  label: string;
  value: any;
};

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'table-filter-input',
  templateUrl: 'table-filter-input.component.html',
  styleUrls: ['table-filter-input.component.scss']
})
export class TableFilterInputComponent implements OnChanges, OnDestroy {

  @Input() type: FilterInputType = FilterType.STRING;
  @Input() placeholder = 'Filtrar';

  @Input() filterValue: any = '';

  @Input() options: FilterInputOption[] = [];
  @Input() options$: Observable<FilterInputOption[]>;

  @Output() filterChange = new EventEmitter<any>();

  internalValue: any = '';
  asyncOptions: FilterInputOption[] = [];

  dateControl = new FormControl<Date | null>(null);

  dateRangeForm = new FormGroup({
    start: new FormControl<Date | null>(null),
    end: new FormControl<Date | null>(null)
  });

  private debounceTimeout: ReturnType<typeof setTimeout> | null = null;
  private asyncSub: Subscription | null = null;
  private dateRangeSub: Subscription | null = null;
  private dateSub: Subscription | null = null;

  protected readonly FilterType = FilterType;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filterValue']) {
      this.syncFilterValue(changes['filterValue']);
    }
    if (changes['type'] && this.type === FilterType.DATE_RANGE) {
      this.subscribeDateRange();
    }
    if (changes['options$'] && this.options$) {
      this.reloadAsyncOptions();
    }
    if (changes['type'] && this.type === FilterType.DATE) {
      this.subscribeDatePicker();
    }
  }

  ngOnDestroy(): void {
    if (this.debounceTimeout) clearTimeout(this.debounceTimeout);
    this.asyncSub?.unsubscribe();
    this.dateRangeSub?.unsubscribe();
    this.dateSub?.unsubscribe();
  }

  onStringInput(value: string): void {
    if (value === this.internalValue) return;

    this.internalValue = value;

    if (this.debounceTimeout) clearTimeout(this.debounceTimeout);

    if (!value) {
      this.filterChange.emit('');
      return;
    }

    this.debounceTimeout = setTimeout(() => {
      this.filterChange.emit(this.internalValue);
      this.debounceTimeout = null;
    }, 1200);
  }

  onSelectChange(value: any): void {
    this.internalValue = value;
    this.filterChange.emit(value);
  }

  onDateChange(value: string): void {
    this.internalValue = value;
    this.filterChange.emit(value);
  }

  private syncFilterValue(change: SimpleChange): void {
    const incoming = this.normalizeValue(change.currentValue);

    if (this.isEmpty(incoming)) {
      this.clearInternalValue();
      return;
    }

    this.restoreInternalValue(incoming);
  }

  private clearInternalValue(): void {
    this.internalValue = this.isSelectType() ? null : '';

    if (this.type === FilterType.DATE_RANGE) {
      this.dateRangeForm.reset(
        {
          start: null,
          end: null
        },
        { emitEvent: false }
      );
    }

    if (this.type === FilterType.DATE) {
      this.dateControl.setValue(null, { emitEvent: false });
    }
  }

  private restoreInternalValue(incoming: any): void {
    this.internalValue = structuredClone(incoming);
    if (this.isDateRangeValue(incoming)) {
      this.dateRangeForm.setValue(
        { start: this.parseLocalDate(incoming[0]), end: this.parseLocalDate(incoming[1]) },
        { emitEvent: false }
      );
    }
    if (this.type === FilterType.DATE && typeof incoming === 'string') {
      this.dateControl.setValue(this.parseLocalDate(incoming), { emitEvent: false });
    }
  }

  private subscribeDateRange(): void {
    this.dateRangeSub?.unsubscribe();
    this.dateRangeSub = this.dateRangeForm.valueChanges.subscribe(({ start, end }) => {
      if (start && end) {
        const fmt = (d: Date) => d.toISOString().substring(0, 10);
        this.filterChange.emit([fmt(start), fmt(end)]);
      } else if (!start && !end) {
        this.filterChange.emit('');
      }
    });
  }

  private subscribeDatePicker(): void {
    this.dateSub?.unsubscribe();
    this.dateSub = this.dateControl.valueChanges.subscribe(value => {
      if (value) {
        const fmt = (d: Date) => {
          const y = d.getFullYear();
          const m = String(d.getMonth() + 1).padStart(2, '0');
          const d2 = String(d.getDate()).padStart(2, '0');
          return `${y}-${m}-${d2}`;
        };
        this.filterChange.emit(fmt(value));
      } else {
        this.filterChange.emit('');
      }
    });
  }

  private parseLocalDate(dateStr: string): Date {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  private reloadAsyncOptions(): void {
    this.asyncSub?.unsubscribe();
    this.asyncSub = this.options$.subscribe({
      next: opts => this.asyncOptions = opts
    });
  }

  private isSelectType(): boolean {
    return this.type === FilterType.SELECT || this.type === FilterType.SELECT_ASYNC;
  }

  private isDateRangeValue(value: any): boolean {
    return this.type === FilterType.DATE_RANGE && Array.isArray(value) && value.length === 2;
  }

  private normalizeValue(value: any): any {
    if (this.isSelectType()) {
      return value === '' || value === undefined ? null : value;
    }

    return value ?? '';
  }

  private isEmpty(value: any): boolean {
    return value === null || value === undefined || value === '';
  }

  compareValues(a: any, b: any): boolean {
    return a === b;
  }
}
